import { Box } from '@mui/material';
import { useState } from 'react';
import { AccordionCollapse } from 'react-bootstrap';
import { generatePath } from 'react-router';

import { ActionsApi } from 'Api/Actions/ActionsApi';
import { ArtificialIntelligenceApi } from 'Api/ArtificialIntelligence/ArtificialIntelligenceApi';
import { DocumentApi } from 'Api/Document/DocumentApi';
import { Accordion } from 'Components/Accordion/Accordion';
import { AccordionToggle } from 'Components/Accordion/AccordionToggle/AccordionToggle';
import { Button } from 'Components/Buttons/Buttons';
import { useCachedData } from 'Components/Context/CachedDataContext';
import { FileManagementArea, useFileManagementArea } from 'Components/Files/FileManagementArea';
import { FormFieldDatePicker } from 'Components/FormField/FormFieldDatePicker/FormFieldDatePicker';
import { FormFieldMultiOptionSelect } from 'Components/FormField/FormFieldMultiOptionSelect/FormFieldMultiOptionSelect';
import { ChangeEventType, FormFieldSelect } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { FormFieldText } from 'Components/FormField/FormFieldText/FormFieldText';
import { FormFieldTextArea } from 'Components/FormField/FormFieldTextArea/FormFieldTextArea';
import { FormFieldTextAreaAi } from 'Components/FormField/FormFieldTextAreaAi/FormFieldTextAreaAi';
import { FormFieldUserMultiSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserMultiSelect';
import { FormFieldUserSelect } from 'Components/FormField/FormFieldUserSelect/FormFieldUserSelect';
import { ConfirmationModal, ConfirmationModalOperationType } from 'Components/Modal/ConfirmationModal/ConfirmationModal';
import { Text } from 'Components/Text/Text';
import { LinkButtonToast, TextToast } from 'Components/Toast/Toast';
import { ICON_DELETE_REMOVE } from 'Config/Icons';
import { ACTION_DETAILS, RISK_DETAILS } from 'Config/PathTemplates';
import { ACTIONS_OWNER, REFERENCE } from 'Config/Tooltips';
import { iso8601ToJsDate, jsDateToIso8601 } from 'Helpers/DateTimeUtils/DateTimeUtils';
import { RISK_ROLES } from 'Helpers/UserUtils';
import { useAiContentAdvisor } from 'Hooks/AiContentAdvisor';
import { Action, ActionStatus, ActionStatusOptions, CreateActionRequest, UpdateActionRequest } from 'Models/Actions';
import { AiContentAdvisorFeature } from 'Models/ArtificialIntelligence';
import { ValidationError } from 'Models/ErrorTypes';
import { RiskResponse } from 'Models/RiskRegister';
import { GroupOptionType } from 'Models/Types/GlobalType';
import { UserResponse } from 'Models/User';
import { RiskTab } from 'Pages/RiskRegister/RiskDetails/RiskDetails';
import { RiskMapping } from 'Pages/RiskRegister/RiskMapping/RiskMapping';

import styles from './ManageActionForm.module.css';
import { ActionsPageType } from '../ManageAction/ManageAction';

enum Modal {
    Delete,
    None,
}

enum SubmitState {
    None,
    Saving,
    Saved,
    Deleted,
}

export interface ManageActionFormProps {
    actionsApi: ActionsApi;
    artificialIntelligenceApi: ArtificialIntelligenceApi;
    defaultMappedRiskIds: string[];
    defaultActionId?: string;
    documentApi: DocumentApi;
    onActionDeleted: () => void;
    pageType: ActionsPageType;
    risks: RiskResponse[];
    initialVersion?: Action;
    preselectedRisk?: RiskResponse;
}

export const ManageActionForm = (props: ManageActionFormProps) => {
    const cachedData = useCachedData();
    const [submitRequestWithManagedFiles, fileManagementHookValues] = useFileManagementArea(props.documentApi, props.initialVersion?.documents ?? []);

    const [actionId, setActionId] = useState<string | undefined>(props.defaultActionId);
    const [mappedRiskIds, setMappedRiskIds] = useState<string[]>(props.defaultMappedRiskIds);
    const [description, setDescription] = useState<string>(props.initialVersion?.description ? props.initialVersion.description : '');
    const [owner, setOwner] = useState<UserResponse | undefined>(cachedData.users.find((user) => user.cognito_subject === props.initialVersion?.owner));
    const [status, setStatus] = useState<ActionStatus | undefined>(props.initialVersion?.status);
    const [tags, setTags] = useState<string[]>(props.initialVersion?.tags ?? []);
    const [title, setTitle] = useState<string>(props.initialVersion?.title ? props.initialVersion.title : '');
    const [comments, setComments] = useState<string>(props.initialVersion?.comments ? props.initialVersion.comments : '');
    const [delegates, setDelegates] = useState<UserResponse[]>(props.initialVersion?.delegates ? cachedData.users.filter((user) => props.initialVersion!.delegates.includes(user.cognito_subject)) : []);
    const [dueDate, setDueDate] = useState<Date | undefined>(props.initialVersion?.due_date ? iso8601ToJsDate(props.initialVersion.due_date) : undefined);
    const [link, setLink] = useState<string>(props.initialVersion?.link ? props.initialVersion.link : '');

    const [displayedModal, setDisplayedModal] = useState(Modal.None);
    const [toastErrorMessage, setToastErrorMessage] = useState<string>();
    const [toastSuccessMessage, setToastSuccessMessage] = useState<string>();
    const [submitState, setSubmitState] = useState(SubmitState.None);
    const descriptionAi = useAiContentAdvisor(props.artificialIntelligenceApi, description, AiContentAdvisorFeature.RISK_MANAGEMENT_ACTION_DESCRIPTION, setToastErrorMessage);

    const validateFormInput = () => {
        if (title === undefined || title.length === 0) {
            throw new ValidationError('Title is required.');
        }

        if (description === undefined || description.length === 0) {
            throw new ValidationError('Description is required.');
        }

        if (owner === undefined) {
            throw new ValidationError('Owner is required.');
        }
    };

    const saveAction = async (event: React.FormEvent<HTMLFormElement>): Promise<void> => {
        event.preventDefault();
        setToastErrorMessage(undefined);
        setToastSuccessMessage(undefined);
        setSubmitState(SubmitState.Saving);

        try {
            validateFormInput();
            await submitRequestWithManagedFiles(async (fileUpdates) => {
                if (actionId && props.pageType === ActionsPageType.MANAGE) {
                    const updateActionRequest: UpdateActionRequest = {
                        associated_risks: mappedRiskIds && mappedRiskIds.length > 0 ? mappedRiskIds : [],
                        description: description!,
                        file_updates: fileUpdates,
                        owner: owner!.cognito_subject,
                        status: status!,
                        tags: tags,
                        title: title!,
                        comments: comments,
                        delegates: delegates.map((delegate) => delegate.cognito_subject),
                        due_date: dueDate ? jsDateToIso8601(dueDate) : undefined,
                        link: link,
                    };
                    await props.actionsApi.updateAction(actionId, updateActionRequest);
                } else {
                    const createActionRequest: CreateActionRequest = {
                        associated_risks: mappedRiskIds && mappedRiskIds.length > 0 ? mappedRiskIds : [],
                        description: description!,
                        owner: owner!.cognito_subject,
                        tags: tags,
                        title: title!,
                        comments: comments,
                        delegates: delegates.map((delegate) => delegate.cognito_subject),
                        due_date: dueDate ? jsDateToIso8601(dueDate) : undefined,
                        file_updates: fileUpdates,
                        link: link,
                    };
                    const createActionResponse = await props.actionsApi.createAction(createActionRequest);
                    setActionId(createActionResponse.data);
                }
            });
            setSubmitState(SubmitState.Saved);

            let successMessage = 'Action saved.';

            if (mappedRiskIds && mappedRiskIds.length > 0) {
                if (mappedRiskIds.length > 1) {
                    successMessage += ' It may take a few seconds for the action to show up in the risk treatment plans.';
                } else {
                    successMessage += ' It may take a few seconds for the action to show up in the risk treatment plan.';
                }
            }
            setToastSuccessMessage(successMessage);
        } catch (error) {
            setToastErrorMessage(error.message);
            setSubmitState(SubmitState.None);
        }
    };

    const deleteAction = async (): Promise<string> => {
        await props.actionsApi.deleteAction(actionId!);
        setSubmitState(SubmitState.Deleted);
        props.onActionDeleted();
        return 'Action deleted.';
    };

    const formDisabled = submitState === SubmitState.Saved || submitState === SubmitState.Deleted;

    return (
        <>
            {displayedModal === Modal.Delete && props.initialVersion && (
                <ConfirmationModal operationType={ConfirmationModalOperationType.DELETE_AFTER_TYPING_TO_CONFIRM} headerText="Delete Action" areYouSureText={`Are you sure you want to delete "${props.initialVersion.title}"?`} performOperation={deleteAction} hideModal={() => setDisplayedModal(Modal.None)}>
                    <Text>The action will be removed from all risk treatment plans that include it.</Text>
                </ConfirmationModal>
            )}
            {toastSuccessMessage && <LinkButtonToast variant="success" clearToast={() => setToastSuccessMessage(undefined)} text={toastSuccessMessage} linkButtonText={props.preselectedRisk ? 'View Treatment Plan' : 'View Action'} linkButtonTo={props.preselectedRisk ? `${generatePath(RISK_DETAILS, { risk_id: props.preselectedRisk.id })}#${RiskTab.TREATMENT_PLAN}` : generatePath(ACTION_DETAILS, { action_id: actionId! })} />}
            {toastErrorMessage && <TextToast variant="failure" clearToast={() => setToastErrorMessage(undefined)} text={toastErrorMessage} />}

            <Box component="form" onSubmit={saveAction} noValidate>
                <div className={styles.formFieldsContainer}>
                    <fieldset disabled={formDisabled}>
                        <div className={styles.fieldGroupContainer}>
                            <div className={styles.fieldContainer}>
                                <FormFieldText formFieldLabel="Title" formFieldId="title" handleChange={(event: React.FormEvent<HTMLInputElement>) => setTitle(event.currentTarget.value)} value={title} required />
                            </div>
                            <div className={styles.fieldContainer}>
                                <FormFieldText formFieldLabel="Reference" formFieldId="reference" handleChange={(event: React.FormEvent<HTMLInputElement>) => setLink(event.currentTarget.value)} value={link} tooltip={REFERENCE} />
                            </div>
                        </div>
                        <div className={styles.fieldGroupContainer}>
                            <div className={styles.fieldContainer}>
                                <FormFieldUserSelect rolesRestriction={RISK_ROLES} onUserSelected={(user?: UserResponse) => setOwner(user)} formFieldId="owner" selectedUser={owner} formFieldLabel="Owner" required tooltip={ACTIONS_OWNER} disabled={formDisabled} />
                            </div>
                            <div className={styles.fieldContainer}>
                                <FormFieldUserMultiSelect selectedUsers={delegates} formFieldId="delegates" formFieldLabel="Delegates" onUsersSelected={(users) => setDelegates(users ?? [])} disabled={formDisabled} />
                            </div>
                        </div>
                        <div className={styles.fieldGroupContainer}>
                            <div className={styles.fieldContainer}>
                                <FormFieldMultiOptionSelect
                                    defaultSelectedOptions={cachedData
                                        .getTagCategoriesGroupedOptions()
                                        .map((group) => group.options)
                                        .flat()
                                        .filter((option) => tags.includes(option.value as string))}
                                    formFieldLabel="Tags"
                                    formFieldId="tags"
                                    handleChange={(value: GroupOptionType[]) => setTags(value.map((tag) => tag.value as string))}
                                    options={cachedData.getTagCategoriesGroupedOptions()}
                                    accessibilityLabel="Tags"
                                    disabled={formDisabled}
                                />
                            </div>
                            <div className={styles.fieldContainer}>
                                <FormFieldDatePicker formFieldLabel="Due Date" formFieldId="dueDate" dateFormat="MM/dd/yyyy" placeholder={'MM/DD/YYYY'} selected={dueDate} handleChange={setDueDate} />
                            </div>
                            {props.pageType === ActionsPageType.MANAGE && (
                                <div className={styles.fieldContainer}>
                                    <FormFieldSelect required formFieldLabel="Status" formFieldId="status" handleChange={(value: ChangeEventType) => setStatus(value as ActionStatus)} options={ActionStatusOptions} selectedOption={status} disabled={formDisabled} />
                                </div>
                            )}
                        </div>
                        <div className={styles.fieldContainer}>
                            <FormFieldTextAreaAi formFieldLabel="Description" formFieldId="description" handleChange={(event: React.FormEvent<HTMLInputElement>) => setDescription(event.currentTarget.value)} value={description} required aiContentAdvisorValues={descriptionAi} />
                        </div>
                        <div className={styles.fieldContainer}>
                            <FormFieldTextArea formFieldId="comments" formFieldLabel="Comments" handleChange={(event: React.FormEvent<HTMLInputElement>) => setComments(event.currentTarget.value)} value={comments} />
                        </div>
                    </fieldset>
                    <div className={styles.dragAndDrop}>
                        <FileManagementArea disabled={formDisabled} documentApi={props.documentApi} fileManagementHookValues={fileManagementHookValues} />
                    </div>
                </div>
                <fieldset disabled={formDisabled}>
                    <div className={styles.riskMapping}>
                        {/* Expand the accordion by default if the action starts with mapped risks, or if it is being created for a specific risk. */}
                        <Accordion defaultActiveKey={props.preselectedRisk || mappedRiskIds.length > 0 ? 'riskTreatments' : undefined}>
                            <div className={styles.accordionContainer}>
                                <AccordionToggle eventKey="riskTreatments" ariaLabelSuffix="Map to Risk Treatment Plan" />
                                <div className={styles.accordionHeaderContainer}>
                                    <Text variant="Header2">Map to Risk Treatment Plan</Text>
                                </div>
                            </div>
                            <hr />
                            <AccordionCollapse eventKey="riskTreatments">
                                <div className={styles.accordionCollapse}>
                                    <RiskMapping
                                        riskMappingItems={props.risks}
                                        handleRiskChange={(unmappedSelectedRisks) => {
                                            setMappedRiskIds(unmappedSelectedRisks);
                                        }}
                                        currentMappedRiskIdentifiers={mappedRiskIds}
                                    />
                                </div>
                            </AccordionCollapse>
                        </Accordion>
                    </div>
                    <div className={styles.buttonContainer}>
                        {props.pageType === ActionsPageType.MANAGE && (
                            <Button variant="danger" disabled={submitState !== SubmitState.None} onClick={() => setDisplayedModal(Modal.Delete)} fontAwesomeImage={ICON_DELETE_REMOVE}>
                                Delete
                            </Button>
                        )}
                        <div className={styles.submitButton}>
                            <Button variant="submit" disabled={submitState !== SubmitState.None} isLoading={submitState === SubmitState.Saving} loadingText="Submitting...">
                                Save
                            </Button>
                        </div>
                    </div>
                </fieldset>
            </Box>
        </>
    );
};
